import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { format, eachDayOfInterval, isToday } from 'date-fns';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';
const API_BASE_URL = `${API_URL}/api`;

const AccommodationSummary = ({
  projects,
  customDateRange,
  onProjectSelect,
  onDateRangeChange,
  onOpenDateRangeModal
}) => {
  console.log('🏠 AccommodationSummary render', { projectsLength: projects.length });

  const [projectsData, setProjectsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Function to get ISO week number (same as in AccommodationCalendar)
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Generate days array from date range
  const days = useMemo(() => {
    if (!customDateRange.start || !customDateRange.end) return [];
    return eachDayOfInterval({ start: customDateRange.start, end: customDateRange.end });
  }, [customDateRange]);

  // Group days by week (same as in AccommodationCalendar)
  const groupDaysByWeek = useCallback((days) => {
    const weeks = [];
    let currentWeek = [];

    days.forEach((day, index) => {
      currentWeek.push(day);

      // If it's Sunday (end of week) or last day, push the week
      if (day.getDay() === 0 || index === days.length - 1) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });

    return weeks;
  }, []);

  const weeks = useMemo(() => groupDaysByWeek(days), [days, groupDaysByWeek]);

  // Fetch data for all projects
  const fetchAllProjectsData = async () => {
    console.log('🏠 fetchAllProjectsData called', { projectsLength: projects.length });

    if (projects.length === 0) {
      console.log('🏠 fetchAllProjectsData - no projects, returning');
      setLoading(false);
      return;
    }

    setIsRefreshing(true);
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Calculate date range for fetching data
    const startDate = customDateRange.start;
    const endDate = customDateRange.end;

    // Extend travel date range
    const travelStartDate = new Date(startDate);
    travelStartDate.setDate(travelStartDate.getDate() - 30);
    const travelEndDate = new Date(endDate);
    travelEndDate.setDate(travelEndDate.getDate() + 30);

    // Fetch travel data once for all projects
    let allTravels = [];
    try {
      const travelRes = await axios.get(`${API_BASE_URL}/travel/`, {
        params: {
          start_date: format(travelStartDate, 'yyyy-MM-dd'),
          end_date: format(travelEndDate, 'yyyy-MM-dd')
        },
        headers
      });
      allTravels = travelRes.data.filter(travel =>
        travel.travel_type === 'check_in' || travel.travel_type === 'check_out'
      );
      console.log('🏠 Fetched travels:', allTravels.length);
    } catch (error) {
      console.error('Error fetching travels:', error);
    }

    const newProjectsData = {};

    for (const project of projects) {
      try {
        // Fetch accommodations for this project
        console.log(`🏠 Fetching data for project ${project.id} (${project.name})`);
        const accommodationsResponse = await axios.get(
          `${API_BASE_URL}/accommodation/`,
          {
            headers,
            params: {
              project: project.id,
              start_date: format(startDate, 'yyyy-MM-dd'),
              end_date: format(endDate, 'yyyy-MM-dd')
            }
          }
        );
        const accommodations = accommodationsResponse.data;

        // Extract all room assignments from accommodations (same as AccommodationCalendar)
        const assignments = [];
        accommodations.forEach(accommodation => {
          if (accommodation.rooms) {
            accommodation.rooms.forEach(room => {
              if (room.current_assignments) {
                room.current_assignments.forEach(assignment => {
                  assignments.push({
                    ...assignment,
                    accommodation: accommodation.id,
                    accommodation_id: accommodation.id,
                    room_id: room.id,
                    room_number: room.room_number,
                    room_capacity: room.capacity
                  });
                });
              }
            });
          }
        });

        console.log(`🏠 Project ${project.name}: ${accommodations.length} accommodations, ${assignments.length} room assignments`);

        // Fetch resource planning assignments for this project
        const resourceAssignmentsResponse = await axios.get(`${API_BASE_URL}/resource-planning-assignments/`, {
          params: {
            project: project.id,
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd')
          },
          headers
        });

        // Process assignments with travel days (same logic as AccommodationCalendar)
        const projectAssignments = resourceAssignmentsResponse.data.map(assignment => {
          const userId = assignment.user;
          const assignmentStart = new Date(assignment.start_date);
          const assignmentEnd = new Date(assignment.end_date);

          const dayBeforeStart = new Date(assignmentStart);
          dayBeforeStart.setDate(dayBeforeStart.getDate() - 1);
          const dayAfterEnd = new Date(assignmentEnd);
          dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);

          const relevantTravels = allTravels
            .filter(travel => {
              if (travel.user_id !== userId) return false;
              const travelDateRaw = new Date(travel.start_date);
              const travelDate = new Date(travelDateRaw.getFullYear(), travelDateRaw.getMonth(), travelDateRaw.getDate());
              const beforeStart = new Date(dayBeforeStart.getFullYear(), dayBeforeStart.getMonth(), dayBeforeStart.getDate());
              const afterEnd = new Date(dayAfterEnd.getFullYear(), dayAfterEnd.getMonth(), dayAfterEnd.getDate());
              return travelDate >= beforeStart && travelDate <= afterEnd;
            })
            .map(travel => ({
              travel_date: travel.start_date,
              travel_type: travel.travel_type,
              needsAccommodation: true
            }));

          return {
            user_data: assignment.user_data,
            role: assignment.role,
            assignments: [assignment],
            travelDays: relevantTravels,
            start_date: assignment.start_date,
            end_date: assignment.end_date,
            assignment_id: assignment.id
          };
        });

        console.log(`🏠 Project ${project.name}: ${projectAssignments.length} resource assignments`);

        newProjectsData[project.id] = {
          accommodations,
          assignments,
          projectAssignments
        };
      } catch (error) {
        console.error(`Error fetching data for project ${project.id}:`, error);
        newProjectsData[project.id] = {
          accommodations: [],
          assignments: [],
          projectAssignments: []
        };
      }
    }

    console.log('🏠 All projects data fetched:', newProjectsData);
    setProjectsData(newProjectsData);
    setLoading(false);
    setIsRefreshing(false);
  };

  useEffect(() => {
    console.log('🏠 useEffect triggered', { projectsLength: projects.length });
    fetchAllProjectsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.length, customDateRange.start?.getTime(), customDateRange.end?.getTime()]);

  // Helper function to check if room is available on a specific date
  const isRoomAvailableOnDate = (room, date) => {
    if (!room.is_active) return false;

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Check new availability_periods first
    if (room.availability_periods && room.availability_periods.length > 0) {
      return room.availability_periods.some(period => {
        const periodStart = new Date(period.start_date);
        const periodEnd = new Date(period.end_date);
        const periodStartOnly = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate());
        const periodEndOnly = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate());
        return dateOnly >= periodStartOnly && dateOnly <= periodEndOnly;
      });
    }

    // Fallback to old available_from/available_to fields
    if (room.available_from) {
      const availableFrom = new Date(room.available_from);
      const availableFromOnly = new Date(availableFrom.getFullYear(), availableFrom.getMonth(), availableFrom.getDate());
      if (dateOnly < availableFromOnly) return false;
    }
    if (room.available_to) {
      const availableTo = new Date(room.available_to);
      const availableToOnly = new Date(availableTo.getFullYear(), availableTo.getMonth(), availableTo.getDate());
      if (dateOnly > availableToOnly) return false;
    }

    return true;
  };

  // Calculate daily summary for a project
  const calculateDailySummary = (projectId, day) => {
    const data = projectsData[projectId];
    if (!data) {
      return { totalCapacity: 0, totalOccupied: 0, peopleNeedingAccommodation: 0, accommodatedWithoutAssignment: [] };
    }

    const { accommodations, assignments, projectAssignments } = data;

    // Debug log for first day only
    if (day.getDate() === days[0]?.getDate() && day.getMonth() === days[0]?.getMonth()) {
      console.log(`🏠 calculateDailySummary for project ${projectId}:`, {
        accommodationsCount: accommodations.length,
        assignmentsCount: assignments.length,
        projectAssignmentsCount: projectAssignments.length,
        day: format(day, 'yyyy-MM-dd')
      });
    }
    const currentDateOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());

    // Count total available beds
    let totalCapacity = 0;
    let totalOccupied = 0;

    accommodations.forEach(accommodation => {
      const rooms = accommodation.rooms || [];
      rooms.forEach(room => {
        if (isRoomAvailableOnDate(room, day)) {
          totalCapacity += room.capacity || 1;
        }
      });

      // Count occupied beds using half-day logic
      const dayAssignments = assignments.filter(a => {
        const room = (accommodation.rooms || []).find(r => r.id === a.room);
        if (!room) return false;
        const assignmentStart = new Date(a.start_date);
        const assignmentEnd = new Date(a.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
        return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
      });

      dayAssignments.forEach(assignment => {
        const assignmentStart = new Date(assignment.start_date);
        const assignmentEnd = new Date(assignment.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

        const isCheckOutDay = currentDateOnly.getTime() === assignmentEndOnly.getTime();
        const isCheckInDay = currentDateOnly.getTime() === assignmentStartOnly.getTime();
        const isSingleDay = assignmentStartOnly.getTime() === assignmentEndOnly.getTime();

        if (isSingleDay) {
          totalOccupied += 1;
        } else if (isCheckOutDay && !isCheckInDay) {
          totalOccupied += 0;
        } else {
          totalOccupied += 1;
        }
      });
    });

    // Count people needing accommodation
    const peopleNeedingAccommodation = projectAssignments.filter(userWithAssignments => {
      const checkOutTravel = userWithAssignments.travelDays?.find(
        travel => travel.travel_type === 'check_out'
      );
      const checkOutTravelDate = checkOutTravel
        ? new Date(new Date(checkOutTravel.travel_date).getFullYear(),
                   new Date(checkOutTravel.travel_date).getMonth(),
                   new Date(checkOutTravel.travel_date).getDate())
        : null;

      const needsAccommodationFromProject = (userWithAssignments.assignments || []).some(assignment => {
        const assignmentStart = new Date(assignment.start_date);
        const assignmentEnd = new Date(assignment.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

        if (currentDateOnly < assignmentStartOnly || currentDateOnly > assignmentEndOnly) {
          return false;
        }

        const isLastDayOfAssignment = currentDateOnly.getTime() === assignmentEndOnly.getTime();
        const isFirstDayOfAssignment = currentDateOnly.getTime() === assignmentStartOnly.getTime();
        const isSingleDay = assignmentStartOnly.getTime() === assignmentEndOnly.getTime();

        const dayAfterAssignment = new Date(assignmentEndOnly);
        dayAfterAssignment.setDate(dayAfterAssignment.getDate() + 1);
        const hasCheckOutTravelNextDay = checkOutTravelDate &&
          checkOutTravelDate.getTime() === dayAfterAssignment.getTime();

        const isActualCheckOutDay = isLastDayOfAssignment && !hasCheckOutTravelNextDay;

        if (isSingleDay && !hasCheckOutTravelNextDay) return false;
        if (isSingleDay && hasCheckOutTravelNextDay) return true;
        if (isActualCheckOutDay && !isFirstDayOfAssignment) return false;
        return true;
      });

      const hasCheckInTravelOnDay = userWithAssignments.travelDays?.some(travel => {
        if (travel.travel_type !== 'check_in') return false;
        const travelDate = new Date(travel.travel_date);
        const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
        return travelDateOnly.getTime() === currentDateOnly.getTime();
      });

      return needsAccommodationFromProject || hasCheckInTravelOnDay;
    }).length;

    // Find people who have room assignment but NO project assignment on this day
    const accommodatedWithoutAssignment = [];

    // Get all users with room assignments on this day
    const usersWithRoomOnDay = new Map(); // userId -> user info

    accommodations.forEach(accommodation => {
      const dayAssignments = assignments.filter(a => {
        const room = (accommodation.rooms || []).find(r => r.id === a.room);
        if (!room) return false;
        const assignmentStart = new Date(a.start_date);
        const assignmentEnd = new Date(a.end_date);
        const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
        const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());

        // Check if assignment covers this day (excluding checkout day using half-day logic)
        if (currentDateOnly < assignmentStartOnly || currentDateOnly > assignmentEndOnly) {
          return false;
        }

        const isCheckOutDay = currentDateOnly.getTime() === assignmentEndOnly.getTime();
        const isCheckInDay = currentDateOnly.getTime() === assignmentStartOnly.getTime();
        const isSingleDay = assignmentStartOnly.getTime() === assignmentEndOnly.getTime();

        // On checkout day (not single day, not also check-in), room is free
        if (isCheckOutDay && !isCheckInDay && !isSingleDay) {
          return false;
        }

        return true;
      });

      dayAssignments.forEach(assignment => {
        const userId = assignment.user || assignment.user_id;
        if (userId && !usersWithRoomOnDay.has(userId)) {
          usersWithRoomOnDay.set(userId, {
            userId,
            userName: assignment.user_name || assignment.user_data?.full_name || `User ${userId}`,
            roomNumber: assignment.room_number,
            accommodationName: accommodation.name
          });
        }
      });
    });

    // Check which of these users have NO project assignment on this day
    usersWithRoomOnDay.forEach((userInfo, oderId) => {
      const hasProjectAssignment = projectAssignments.some(pa => {
        const paUserId = pa.user_data?.id;
        if (paUserId !== userInfo.userId) return false;

        // Check if any of user's assignments cover this day
        return (pa.assignments || []).some(assignment => {
          const assignmentStart = new Date(assignment.start_date);
          const assignmentEnd = new Date(assignment.end_date);
          const assignmentStartOnly = new Date(assignmentStart.getFullYear(), assignmentStart.getMonth(), assignmentStart.getDate());
          const assignmentEndOnly = new Date(assignmentEnd.getFullYear(), assignmentEnd.getMonth(), assignmentEnd.getDate());
          return currentDateOnly >= assignmentStartOnly && currentDateOnly <= assignmentEndOnly;
        });
      });

      // Also check if user has check-in travel on this day (they need room even without assignment)
      const hasCheckInTravelOnDay = projectAssignments.some(pa => {
        const paUserId = pa.user_data?.id;
        if (paUserId !== userInfo.userId) return false;

        return pa.travelDays?.some(travel => {
          if (travel.travel_type !== 'check_in') return false;
          const travelDate = new Date(travel.travel_date);
          const travelDateOnly = new Date(travelDate.getFullYear(), travelDate.getMonth(), travelDate.getDate());
          return travelDateOnly.getTime() === currentDateOnly.getTime();
        });
      });

      if (!hasProjectAssignment && !hasCheckInTravelOnDay) {
        accommodatedWithoutAssignment.push(userInfo);
      }
    });

    return { totalCapacity, totalOccupied, peopleNeedingAccommodation, accommodatedWithoutAssignment };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-brand-500 mx-auto mb-4 animate-spin" />
          <p className="text-slate-500">Loading projects summary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with date navigation and refresh button */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0 bg-white">
        <h2 className="text-lg font-bold text-slate-900">All Projects - Accommodation Summary</h2>

        {/* Date Range Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onDateRangeChange && onDateRangeChange('prev')}
            className="p-2 hover:bg-brand-50 rounded-lg transition-colors"
            title="Previous 7 days"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <button
            onClick={() => onOpenDateRangeModal && onOpenDateRangeModal()}
            className="px-3 py-1.5 hover:bg-brand-50 rounded-lg transition-colors text-sm font-bold text-slate-700"
            title="Click to change date range"
          >
            {format(customDateRange.start, 'MMM d, yyyy')} - {format(customDateRange.end, 'MMM d, yyyy')}
          </button>
          <button
            onClick={() => onDateRangeChange && onDateRangeChange('next')}
            className="p-2 hover:bg-brand-50 rounded-lg transition-colors"
            title="Next 7 days"
          >
            <ChevronRight className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        <button
          onClick={fetchAllProjectsData}
          disabled={isRefreshing}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          title="Refresh data"
        >
          <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin text-brand-500' : 'text-slate-500'}`} />
        </button>
      </div>

      {/* Calendar Table */}
      <div className="flex-1 overflow-auto relative" style={{ maxHeight: 'calc(100vh - 180px)' }}>
        <table className="border-collapse w-max" style={{ tableLayout: 'fixed' }}>
          <thead className="sticky top-0 z-20 bg-white">
            {/* Week numbers row */}
            <tr>
              <th
                className="border border-slate-200 bg-steel-100 sticky left-0 z-30"
                style={{ width: '140px', minWidth: '140px', boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }}
              ></th>
              {weeks.map((week, weekIndex) => {
                const weekNumber = getWeekNumber(week[0]);
                return (
                  <th
                    key={weekIndex}
                    className="border border-slate-200 bg-steel-100 text-xs font-bold text-slate-700 py-1 relative"
                    colSpan={week.length}
                    style={{
                      borderLeft: '1px solid #e2e8f0',
                      borderRight: '1px solid #e2e8f0'
                    }}
                  >
                    Week {weekNumber}
                  </th>
                );
              })}
            </tr>
            {/* Days row */}
            <tr>
              <th
                className="border border-slate-200 p-1 bg-steel-100 sticky left-0 z-30 text-slate-900 font-bold text-xs"
                style={{ width: '140px', minWidth: '140px', boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }}
              >
                Project
              </th>
              {days.map(day => (
                <th
                  key={day.toISOString()}
                  className={`border border-slate-200 p-1 text-xs text-slate-700 ${
                    isToday(day) ? 'bg-brand-100' : (day.getDay() === 0 || day.getDay() === 6) ? 'bg-amber-100' : 'bg-steel-100'
                  }`}
                  style={{
                    width: '36px',
                    minWidth: '36px',
                    maxWidth: '36px',
                    boxSizing: 'border-box'
                  }}
                >
                  <div className="font-bold">{format(day, 'd')}</div>
                  <div className="font-normal">{format(day, 'EEE')}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr
                key={project.id}
                style={{ height: '44px' }}
                className="hover:bg-slate-50"
              >
                <td
                  className="border border-slate-200 p-1 bg-white sticky left-0 z-10 cursor-pointer hover:bg-brand-50 transition-colors"
                  style={{
                    width: '140px',
                    minWidth: '140px',
                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                  }}
                  onClick={() => onProjectSelect(project.id)}
                  title={`Click to view ${project.name}`}
                >
                  <div className="font-bold text-xs text-slate-900 truncate">{project.name}</div>
                  <div className="text-xs text-slate-500">
                    {projectsData[project.id]?.accommodations?.length || 0} acc.
                  </div>
                </td>
                {days.map(day => {
                  const summary = calculateDailySummary(project.id, day);
                  const { totalCapacity, totalOccupied, peopleNeedingAccommodation, accommodatedWithoutAssignment } = summary;
                  const availableBeds = totalCapacity - totalOccupied;

                  // Determine cell color
                  // Red: over capacity (needed > capacity) - CRITICAL
                  // Purple: ghost bookings (room assigned but no project work)
                  // Yellow: people without accommodation (people need accommodation but don't have room assigned)
                  // Orange: too many rooms rented (capacity > needed) - wasting money
                  // Green: perfect situation
                  // Split diagonal: combined issues
                  const hasGhostBookings = accommodatedWithoutAssignment.length > 0;
                  // Valid occupied = people in rooms who actually work on project (exclude ghosts)
                  const validOccupied = totalOccupied - accommodatedWithoutAssignment.length;
                  const hasPeopleWithoutAccommodation = validOccupied < peopleNeedingAccommodation;
                  const hasTooManyRooms = totalCapacity > peopleNeedingAccommodation;
                  const isOverCapacity = peopleNeedingAccommodation > totalCapacity;
                  // Perfect: capacity matches needed exactly AND everyone accommodated AND no ghost bookings
                  const isPerfect = totalCapacity === peopleNeedingAccommodation &&
                                    validOccupied === peopleNeedingAccommodation &&
                                    !hasGhostBookings;

                  let bgColor = '';
                  let bgStyle = {};

                  if (isOverCapacity) {
                    bgColor = 'bg-red-300';
                  } else if (isPerfect) {
                    bgColor = 'bg-emerald-100';
                  } else if (hasGhostBookings && hasPeopleWithoutAccommodation) {
                    // Ghost bookings + missing room - diagonal split (purple top-left, yellow bottom-right)
                    bgStyle = {
                      background: 'linear-gradient(135deg, #e9d5ff 50%, #fef08a 50%)'
                    };
                  } else if (hasPeopleWithoutAccommodation && hasTooManyRooms) {
                    // Missing room + too many rooms - diagonal split (yellow top-left, orange bottom-right)
                    bgStyle = {
                      background: 'linear-gradient(135deg, #fef08a 50%, #fed7aa 50%)'
                    };
                  } else if (hasGhostBookings) {
                    // Purple for ghost bookings - room assigned but no project work
                    bgColor = 'bg-purple-200';
                  } else if (hasPeopleWithoutAccommodation) {
                    bgColor = 'bg-yellow-200';
                  } else if (hasTooManyRooms) {
                    bgColor = 'bg-orange-200';
                  } else {
                    bgColor = 'bg-emerald-100'; // No issues
                  }

                  // Build tooltip with ghost bookings info
                  const ghostBookingsInfo = hasGhostBookings
                    ? `\n👻 Ghost bookings - room without project assignment (${accommodatedWithoutAssignment.length}):\n${accommodatedWithoutAssignment.map(u => `  • ${u.userName}`).join('\n')}`
                    : '';

                  const peopleWithoutAccommodationCount = peopleNeedingAccommodation - validOccupied;

                  return (
                    <td
                      key={day.toISOString()}
                      className={`border border-slate-200 p-1 text-center cursor-pointer hover:bg-brand-100 transition-colors ${bgColor}`}
                      style={{ width: '40px', minWidth: '40px', maxWidth: '40px', boxSizing: 'border-box', ...bgStyle }}
                      title={`${project.name} - ${format(day, 'MMM d, yyyy')}\n${isOverCapacity ? '🔴 ' : ''}Beds capacity: ${totalCapacity}\nBeds occupied: ${totalOccupied}${hasGhostBookings ? ` (incl. ${accommodatedWithoutAssignment.length} ghost)` : ''}\nBeds available: ${availableBeds}\n${isOverCapacity ? '🔴 ' : ''}People needing accommodation: ${peopleNeedingAccommodation}${hasPeopleWithoutAccommodation ? `\n⚠️ People without accommodation: ${peopleWithoutAccommodationCount}` : ''}${ghostBookingsInfo}`}
                      onClick={() => onProjectSelect(project.id)}
                    >
                      <div className="text-xs">
                        <div className={`font-bold ${validOccupied >= peopleNeedingAccommodation ? 'text-emerald-600' : 'text-red-600'}`}>
                          {validOccupied}/{peopleNeedingAccommodation}
                        </div>
                        <div className={`font-bold`} style={{ fontSize: '10px', color: hasGhostBookings ? '#7c3aed' : (availableBeds === 0 ? '#059669' : '#ea580c') }}>
                          {hasGhostBookings ? `${accommodatedWithoutAssignment.length}👻` : `${availableBeds} free`}
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {projects.length === 0 && (
              <tr>
                <td colSpan={days.length + 1} className="border border-slate-200 p-8 text-center text-slate-500">
                  No projects found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
        <div className="flex items-center space-x-4 text-xs text-slate-600 font-medium">
          <div
            className="flex items-center space-x-1.5 cursor-help"
            title="Perfect: bed count matches people needing accommodation, everyone has a room assigned"
          >
            <div className="w-4 h-4 bg-emerald-100 border border-slate-300 rounded"></div>
            <span>Perfect</span>
          </div>
          <div
            className="flex items-center space-x-1.5 cursor-help"
            title="Some people are assigned to work on the project but don't have a room assigned yet"
          >
            <div className="w-4 h-4 bg-yellow-200 border border-slate-300 rounded"></div>
            <span>People without accommodation</span>
          </div>
          <div
            className="flex items-center space-x-1.5 cursor-help"
            title="More rooms rented than needed - wasting money on unused beds"
          >
            <div className="w-4 h-4 bg-orange-200 border border-slate-300 rounded"></div>
            <span>Too many rooms</span>
          </div>
          <div
            className="flex items-center space-x-1.5 cursor-help"
            title="Someone has a room assigned but is NOT assigned to work on the project this day - paying for unnecessary accommodation"
          >
            <div className="w-4 h-4 bg-purple-200 border border-slate-300 rounded"></div>
            <span>Ghost booking 👻</span>
          </div>
          <div
            className="flex items-center space-x-1.5 cursor-help"
            title="Combined issues: people without room + too many rooms, or ghost booking + people without room"
          >
            <div className="w-4 h-4 border border-slate-300 rounded" style={{ background: 'linear-gradient(135deg, #fef08a 50%, #fed7aa 50%)' }}></div>
            <span>Combined issues</span>
          </div>
          <div
            className="flex items-center space-x-1.5 cursor-help"
            title="CRITICAL: More people need accommodation than available beds!"
          >
            <div className="w-4 h-4 bg-red-300 border border-slate-300 rounded"></div>
            <span>Over capacity</span>
          </div>
        </div>
        <div className="text-xs text-slate-500">
          Format: <span className="font-mono font-bold text-slate-700">occupied/needed</span>
        </div>
      </div>
    </div>
  );
};

export default AccommodationSummary;
